import { ThemeProvider, CssBaseline } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { RouterProvider } from 'react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { drisTheme } from './theme/drisTheme';
import { AuthProvider } from './contexts/AuthContext';
import { ImpersonationBanner } from './components/Impersonation/ImpersonationBanner';
import { PdfStatusProvider } from './contexts/PdfStatusContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { CommunicationCountsProvider } from './contexts/CommunicationCountsContext';
import { REFERENCE_DATA_STALE_TIME } from './hooks/useReferenceData';
import { router } from './router';
import './styles/dris-theme.css';

// Single app-wide react-query client. Defaults are tuned for the reference/lookup
// lists that currently use it (see hooks/useReferenceData.ts): stay fresh for a few
// minutes and don't refetch just because the window regained focus.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: REFERENCE_DATA_STALE_TIME,
      gcTime: 30 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// App version: 2026.02.15
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={drisTheme} defaultMode="system" disableTransitionOnChange>
        <CssBaseline enableColorScheme />
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          dense
          preventDuplicate
        >
          <AuthProvider>
            {/* Mounted ABOVE the router (H3): no route/redirect/AppLayout early-return can suppress the
                impersonation banner, including the portal-confinement <Navigate> for a boundary target. */}
            <ImpersonationBanner />
            <PdfStatusProvider>
              <NotificationProvider>
                <CommunicationCountsProvider>
                  <RouterProvider router={router} />
                </CommunicationCountsProvider>
              </NotificationProvider>
            </PdfStatusProvider>
          </AuthProvider>
        </SnackbarProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
